/**
 * useConfirmDialog
 *
 * Hook that provides a Promise-based API for the ConfirmDialog component.
 * Allows replacing synchronous window.confirm() calls with async/await.
 *
 * Usage:
 *   const { isOpen, dialogProps, confirm } = useConfirmDialog();
 *   // ...in JSX: <ConfirmDialog {...dialogProps} />
 *   // ...in handler: if (await confirm({ title: '...', message: '...' })) { ... }
 */

import { useState, useCallback, useRef } from 'react';
import type { ConfirmDialogProps, ConfirmDialogVariant } from '../components/common/ConfirmDialog';

// ============================================================================
// Types
// ============================================================================

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmDialogVariant;
}

export interface UseConfirmDialogReturn {
  isOpen: boolean;
  dialogProps: ConfirmDialogProps;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

// ============================================================================
// Hook
// ============================================================================

export function useConfirmDialog(): UseConfirmDialogReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({
    title: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  // Holds the resolve function of the pending Promise
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    setOptions(opts);
    setIsOpen(true);
    setLoading(false);

    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    resolveRef.current?.(true);
    resolveRef.current = null;
  }, []);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    resolveRef.current?.(false);
    resolveRef.current = null;
  }, []);

  const dialogProps: ConfirmDialogProps = {
    isOpen,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
    title: options.title,
    message: options.message,
    confirmText: options.confirmText,
    cancelText: options.cancelText,
    variant: options.variant,
    loading,
  };

  return { isOpen, dialogProps, confirm };
}

export { useConfirmDialog as default };
